import React, { useState } from "react"
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Collapse,
  Badge,
  Card,
  CardBody,
  Row,
  Col,
} from "reactstrap"
import moment from "moment"

const CPSSubVersions = ({ entry, myUser, statusIndex, reload }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedSubVersion, setSelectedSubVersion] = useState(null)
  const [customerOrderNumber, setCustomerOrderNumber] = useState("")
  const [softwareVersion, setSoftwareVersion] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Determine if this step is active based on entry status
  const isActive =
    entry?.status === "Sub-Versions Created" || entry?.isParentRequest
  const isCompleted = ["Production", "Stock Management", "Completed"].includes(
    entry?.status
  )
  const canManage = isActive && myUser?.userType !== "Admin"

  // Demo sub-versions data
  const subVersions = entry?.subRequests || [
    {
      id: "sub-001",
      requestNumber: "CPS-001-001",
      parentRequestNumber: "CPS-001",
      softwareVersion: "v2.1.3",
      customerOrderNumber: "",
      quantity: 200,
      status: "Waiting for Customer Input",
      dateCreated: "2025-01-16",
      isLocked: false,
      notes: "Latest firmware version",
    },
    {
      id: "sub-002",
      requestNumber: "CPS-001-002",
      parentRequestNumber: "CPS-001",
      softwareVersion: "v2.0.5",
      customerOrderNumber: "CUST-002",
      quantity: 300,
      status: "Production",
      dateCreated: "2025-01-17",
      isLocked: true,
      notes: "Legacy version for existing customers",
    },
  ]

  const handleSubVersionSelect = subVersion => {
    setSelectedSubVersion(subVersion)
    setCustomerOrderNumber(subVersion.customerOrderNumber || "")
    setSoftwareVersion(null)
    setIsOpen(true)
  }

  const handleSubmitSubVersion = async e => {
    e.preventDefault()
    if (!selectedSubVersion || !customerOrderNumber) return

    setIsSubmitting(true)

    setTimeout(() => {
      setIsSubmitting(false)
      setIsOpen(false)
      setSelectedSubVersion(null)
      setCustomerOrderNumber("")
      setSoftwareVersion(null)
      alert("Sub-version updated successfully!")
    }, 1500)
  }

  const getStatusColor = status => {
    switch (status) {
      case "Waiting for Customer Input":
        return "warning"
      case "Production":
        return "info"
      case "Completed":
        return "success"
      default:
        return "secondary"
    }
  }

  return (
    <li className={"event-list " + (isActive ? "active" : "")}>
      <div
        className={"event-timeline-dot " + (isActive ? "bx-fade-right" : "")}
      >
        <i className="bx bx-right-arrow-circle font-size-18"></i>
      </div>
      <div className="d-flex">
        <div className="flex-shrink-0 me-3">
          <h5 className="font-size-14">
            {isCompleted ? moment(new Date()).format("DD.MM.YYYY") : ""}{" "}
            <i className="bx bx-right-arrow-alt font-size-16 text-primary align-middle ms-2"></i>
          </h5>
        </div>
        <div className="flex-grow-1">
          <div>
            Sub-Versions Management{" "}
            {isActive
              ? ` - ${subVersions.length} sub-versions created, waiting for customer input`
              : isCompleted
              ? ` - all sub-versions processed`
              : ""}
          </div>
        </div>
      </div>

      {isActive && canManage && (
        <div className="mt-3">
          <div className="mb-3">
            <h6 className="mb-2">Available Sub-Versions</h6>
            <Row>
              {subVersions.map(subVersion => (
                <Col md="6" key={subVersion.id} className="mb-2">
                  <Card
                    className={`h-100 ${
                      subVersion.isLocked ? "opacity-75" : "cursor-pointer"
                    }`}
                    onClick={() =>
                      !subVersion.isLocked && handleSubVersionSelect(subVersion)
                    }
                    style={{
                      cursor: subVersion.isLocked ? "not-allowed" : "pointer",
                    }}
                  >
                    <CardBody className="p-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="mb-0">{subVersion.requestNumber}</h6>
                        <Badge
                          color={getStatusColor(subVersion.status)}
                          size="sm"
                        >
                          {subVersion.status}
                        </Badge>
                      </div>
                      <div className="small text-muted">
                        <div>
                          <strong>Software Version:</strong>{" "}
                          {subVersion.softwareVersion}
                        </div>
                        <div>
                          <strong>Quantity:</strong> {subVersion.quantity} pcs
                        </div>
                        <div>
                          <strong>Customer Order:</strong>{" "}
                          {subVersion.customerOrderNumber || "Not set"}
                        </div>
                        {subVersion.isLocked && (
                          <div className="text-warning">
                            <i className="bx bx-lock me-1"></i>
                            Locked - In Production
                          </div>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>

          <Collapse isOpen={isOpen}>
            <div className="mt-3 p-3 border rounded">
              <h6 className="mb-3">
                Configure Sub-Version: {selectedSubVersion?.requestNumber}
              </h6>
              <Form onSubmit={handleSubmitSubVersion}>
                <FormGroup className="mb-3">
                  <Label>Customer Order Number *</Label>
                  <Input
                    type="text"
                    value={customerOrderNumber}
                    onChange={e => setCustomerOrderNumber(e.target.value)}
                    placeholder="Enter your order number"
                    disabled={isSubmitting}
                  />
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label>Software Version Upload</Label>
                  <Input
                    type="file"
                    onChange={e => setSoftwareVersion(e.target.files[0])}
                    accept=".hex,.bin,.elf,.s19"
                    disabled={isSubmitting}
                  />
                  <div className="form-text text-muted">
                    Upload the software version file (.hex, .bin, .elf, .s19)
                  </div>
                </FormGroup>

                <div className="d-flex gap-2">
                  <Button
                    type="submit"
                    color="primary"
                    size="sm"
                    disabled={!customerOrderNumber || isSubmitting}
                  >
                    {isSubmitting ? "Updating..." : "Update Sub-Version"}
                  </Button>
                  <Button
                    type="button"
                    color="secondary"
                    size="sm"
                    onClick={() => {
                      setIsOpen(false)
                      setSelectedSubVersion(null)
                    }}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            </div>
          </Collapse>
        </div>
      )}

      {isCompleted && (
        <div className="mt-3">
          <small className="text-muted">
            <strong>Total Sub-Versions:</strong> {subVersions.length} <br />
            <strong>Completed:</strong>{" "}
            {subVersions.filter(s => s.status === "Completed").length} <br />
            <strong>In Production:</strong>{" "}
            {subVersions.filter(s => s.status === "Production").length}
          </small>
        </div>
      )}

      <p className="mb-0 mt-2">
        <i className="bx bx-time-five font-size-13 align-middle me-1"></i>
        <span className="font-size-13">
          {isCompleted
            ? "All sub-versions processed"
            : isActive
            ? "Waiting for customer input"
            : "Not started"}
        </span>
      </p>
    </li>
  )
}

export default CPSSubVersions
